import {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';

import { useOffers } from './useOffers';
import { useSplitTestBranch } from '../../api/lendingApi/lendingApiQueries';
import { getMarketingPollUpdatedState } from '../../components/helpers/stateNormalisation';
import { ISOLATED_SPLIT_TESTS } from '../../constants';
import { TGetStateResponseDE } from '../../lapiOverrideTypesDE';
import { useContextStateKey } from '../useContextStateKey';
import { useSignupParams } from '../useSignupParams';
import { useSignupRouterDE } from '../useSignupRouter/useSignupRouterDE';

const OffersDEContext = createContext<
  | {
      signupError: ReturnType<ReturnType<typeof useOffers>['getSignupError']>;
      offers: ReturnType<typeof useOffers>['offers'];
      documentRequirements: ReturnType<
        ReturnType<typeof useOffers>['getDocumentRequirements']
      >;
      handleDecision: () => Promise<void>;
      isDeciding: boolean;
      isLoadingOffers: boolean;
      isRefetchingOffers: boolean;
      isLoadingOrRedirecting: boolean;
      isSyncingState: boolean;
      setIsSyncingState: Dispatch<SetStateAction<boolean>>;
      isInExitPollTestDE: boolean;
    }
  | undefined
>(undefined);

type TProps = {
  children: React.ReactNode;
};

export const OffersDEProvider = ({ children }: TProps) => {
  const [isDeciding, setIsDeciding] = useState(false);
  const [isSyncingState, setIsSyncingState] = useState(false);

  const {
    isLoadingOffers,
    getSignupError,
    offers,
    getDocumentRequirements,
    checkDocumentsRequired,
    refetchOffers,
    isRefetchingOffers,
    offerStatus,
  } = useOffers({ enabled: true });

  const stateKey = useContextStateKey();
  const { navigateSignupDE, navigateToAccountPages, currentStep } =
    useSignupRouterDE();

  const { isDirectCustomer } = useSignupParams();

  const hasDocumentsRequired = checkDocumentsRequired(
    getDocumentRequirements(offers),
  );

  const isOffersOrEligibilityStep =
    currentStep === 'angebote' || currentStep === 'eignungscheck';

  const { splitTestBranch: exitPollBranch, isLoading: exitPollTestLoading } =
    useSplitTestBranch(ISOLATED_SPLIT_TESTS.DE_EXIT_POLL_TEST, {
      enabled: isDirectCustomer,
      cookieOverride: true,
    });
  const isInExitPollTestDE = exitPollBranch === 'INCLUDE';

  const isLoadingOrRedirecting =
    isLoadingOffers ||
    !offers ||
    getSignupError(offers) === 'complete_personal_credit_check' ||
    offerStatus === 'declined' ||
    offerStatus === 'deferred';

  // Only fire exposure on the loading screen (where the poll shows). Offers
  // are cached across the offer-flow steps, so later steps are not loading and
  // do not re-fire, keeping exposure to once per user.
  useEffect(() => {
    if (exitPollBranch && isLoadingOrRedirecting) {
      tracking.ampli.addedToSplitTest({
        Branch: exitPollBranch,
        'Test name': ISOLATED_SPLIT_TESTS.DE_EXIT_POLL_TEST,
      });
    }
  }, [exitPollBranch, isLoadingOrRedirecting]);

  // Redirection useEffect
  useEffect(() => {
    if (
      !isOffersOrEligibilityStep ||
      !stateKey ||
      isLoadingOffers ||
      isRefetchingOffers ||
      !offers ||
      isDeciding ||
      exitPollTestLoading
    ) {
      return;
    }

    const signupError = getSignupError(offers);

    if (signupError && signupError !== 'complete_personal_credit_check') {
      return;
    }

    const updatedState = getMarketingPollUpdatedState<TGetStateResponseDE>();

    if (hasDocumentsRequired) {
      void navigateSignupDE('eignungscheck', stateKey, updatedState);
    } else {
      void navigateToAccountPages(stateKey, updatedState);
    }
  }, [isLoadingOffers, offers, stateKey, exitPollTestLoading]);

  const handleDecision = async () => {
    setIsDeciding(true);

    const refetchedOffers = await refetchOffers();

    const hasRefetchedDocumentsRequired = checkDocumentsRequired(
      getDocumentRequirements(refetchedOffers),
    );

    if (!hasRefetchedDocumentsRequired && stateKey) {
      await navigateToAccountPages(
        stateKey,
        getMarketingPollUpdatedState<TGetStateResponseDE>(),
      );
    }

    setIsDeciding(false);
  };

  return (
    <OffersDEContext.Provider
      value={{
        signupError: getSignupError(offers),
        offers,
        documentRequirements: getDocumentRequirements(offers),
        handleDecision,
        isDeciding,
        isLoadingOffers,
        isRefetchingOffers,
        isLoadingOrRedirecting,
        isSyncingState,
        setIsSyncingState,
        isInExitPollTestDE,
      }}
    >
      {children}
    </OffersDEContext.Provider>
  );
};

export function useOffersDE() {
  const context = useContext(OffersDEContext);
  if (context === undefined) {
    throw new Error('useOffersDE must be used within the OffersDEProvider');
  }
  return context;
}
